import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { Form, Input } from "reactstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useTranslation } from "react-i18next";
import {
  FiAlertCircle,
  FiArrowRight,
  FiAward,
  FiBookOpen,
  FiCalendar,
  FiCheck,
  FiChevronRight,
  FiFilter,
  FiHash,
  FiLayers,
  FiMapPin,
  FiPhone,
  FiRotateCcw,
  FiSearch,
  FiSliders,
  FiUser,
} from "react-icons/fi";

import { ApiUnit } from "../../unit/ApiUnit";
import { requestApi } from "../../../actions/api";
import { ApiResponsibility } from "../../config/responsibilityType/ApiResponsibilityType";
import { DateValidater } from "../../helper/Method";
import { encryptStorage } from "../../../constants/EncryptStorage";
import { isUrdu } from "../../../constants/const";
import { buildAppliedFilters, resetCourseFilter, resetEventFilter } from "./reportFilters";
import { IconInput, SectionTitle, Segmented, Toggle, selectStyles } from "./ReportControls";

const atFiveAm = (date) => {
  const d = new Date(date);
  d.setHours(5, 0, 0, 0);
  return d;
};

const SearchReport = ({ model, defaultModel, onSubmit }) => {
  const { t } = useTranslation();
  const loggedInUnitId = parseInt(encryptStorage.getItem("loggedInUnitId"));
  const [values, setValues] = useState(model);
  const [unitList, setUnitList] = useState([]);
  const [responsibilityOptions, setResponsibilityOptions] = useState([]);
  const [memberTypeOptions, setMemberTypeOptions] = useState([]);
  const [eventTypeOptions, setEventTypeOptions] = useState([]);
  const [courseTypeOptions, setCourseTypeOptions] = useState([]);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    ApiResponsibility()
      .getResponsibilitiesByLoggedInUnit(loggedInUnitId)
      .then((res) => setResponsibilityOptions(res.data.map((d) => ({ value: d.value, label: d.label }))))
      .catch((err) => console.log(err));
    requestApi("eventtype/")
      .fetchAll()
      .then((res) => setEventTypeOptions(res.data.map((d) => ({ value: d.id, label: d.type }))))
      .catch((err) => console.log(err));
    requestApi("membertype/")
      .fetchAll()
      .then((res) => setMemberTypeOptions(res.data.map((d) => ({ value: d.id, label: d.type }))))
      .catch((err) => console.log(err));
    requestApi("coursetype/")
      .fetchAll()
      .then((res) => setCourseTypeOptions(res.data.map((d) => ({ value: d.id, label: d.name }))))
      .catch((err) => console.log(err));
  }, [loggedInUnitId]);

  const showExUnits = values.ui.showExUnits;
  useEffect(() => {
    ApiUnit()
      .getUnitList(values.unitId || loggedInUnitId, loggedInUnitId, false, "null", !showExUnits)
      .then((res) => setUnitList(res.data))
      .catch((err) => console.log(err));
  }, [loggedInUnitId, values.unitId, showExUnits]);

  const selectedUnitName = useMemo(() => {
    const deepest = [...unitList].reverse().find((r) => r.selectedUnit !== null);
    return deepest ? deepest.selectedUnit.name : "";
  }, [unitList]);

  const set = (patch) => setValues((v) => ({ ...v, ...patch }));
  const setUi = (patch) => setValues((v) => ({ ...v, ui: { ...v.ui, ...patch } }));

  const handleInputChange = (e) => set({ [e.target.name]: e.target.value });

  const handleMultiSelect = (idsField, uiField, errorField) => (selected) => {
    const list = selected || [];
    setValues((v) => ({ ...v, [idsField]: list.map((o) => o.value), ui: { ...v.ui, [uiField]: list } }));
    if (errorField) setErrors((e) => ({ ...e, [errorField]: null }));
  };

  const handleChangeUnit = (record) => set({ unitId: record ? record.value : 0 });

  const toggleEvents = (on) => {
    setValues((v) => {
      const cleared = resetEventFilter(v);
      return { ...cleared, ui: { ...cleared.ui, eventsOn: on } };
    });
    setErrors((e) => ({ ...e, eventType: null, eventStartDate: null, eventEndDate: null }));
  };

  const toggleCourses = (on) => {
    setValues((v) => {
      const cleared = resetCourseFilter(v);
      return { ...cleared, ui: { ...cleared.ui, coursesOn: on } };
    });
    setErrors((e) => ({ ...e, courseType: null, courseStartDate: null, courseEndDate: null }));
  };

  // Dates are stored at 05:00 (as before) and the range must not be reversed.
  const handleDate = (field, startField, endField) => (value) => {
    const date = value ? atFiveAm(value) : null;
    const start = field === startField ? date : values[startField];
    const end = field === endField ? date : values[endField];
    if (DateValidater(start, end)) {
      set({ [field]: date });
      setErrors((e) => ({ ...e, [startField]: null, [endField]: null }));
    } else {
      setErrors((e) => ({
        ...e,
        [field]: field === startField ? "common_start_date_validation_message" : "common_end_date_validation_message",
      }));
    }
  };

  const validate = () => {
    const temp = {};
    if (values.ui.eventsOn && values.eventTypeIds.length === 0) temp.eventType = "events_event_type_error_message";
    if (values.ui.coursesOn && values.courseTypeIds.length === 0)
      temp.courseType = "courses_course_type_error_message";
    setErrors((e) => ({ ...e, ...temp }));
    return Object.keys(temp).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) onSubmit({ ...values, ui: { ...values.ui, unitName: selectedUnitName } });
  };

  const handleReset = () => {
    setValues(defaultModel);
    setErrors({});
  };

  const chips = buildAppliedFilters({ ...values, ui: { ...values.ui, unitName: selectedUnitName } });
  const errorCount = Object.values(errors).filter(Boolean).length;

  const textField = (name, label, placeholder, icon, urdu = false) => (
    <div className="mr-field">
      <label htmlFor={"mr-" + name}>{t(label)}</label>
      <IconInput icon={icon}>
        <Input
          id={"mr-" + name}
          name={name}
          value={values[name] || ""}
          placeholder={t(placeholder)}
          className={(values[name] ? "mr-filled " : "") + (urdu || isUrdu(values[name]) ? "ur" : "")}
          onChange={handleInputChange}
          autoComplete="off"
        />
      </IconInput>
    </div>
  );

  const dateField = (field, startField, endField, label) => (
    <div className="mr-field">
      <label>{t(label)}</label>
      <IconInput icon={<FiCalendar size={15} />} error={errors[field]}>
        <DatePicker
          showYearDropdown
          isClearable
          placeholderText="dd/mm/yyyy"
          className={"form-control" + (values[field] ? " mr-filled" : "")}
          dateFormat="dd/MM/yyyy"
          selected={values[field] ? new Date(values[field]) : null}
          onChange={handleDate(field, startField, endField)}
        />
      </IconInput>
      {errors[field] && (
        <div className="mr-err-msg">
          <FiAlertCircle size={13} /> {t(errors[field])}
        </div>
      )}
    </div>
  );

  const multiSelect = (label, options, value, onChange, placeholder, error) => (
    <div className="mr-field">
      <label>{t(label)}</label>
      <Select
        options={options}
        value={value}
        className="basic-multi-select ur"
        classNamePrefix="select"
        styles={selectStyles(!!error)}
        placeholder={t(placeholder)}
        isMulti
        isClearable
        onChange={onChange}
      />
      {error && (
        <div className="mr-err-msg">
          <FiAlertCircle size={13} /> {t(error)}
        </div>
      )}
    </div>
  );

  const rangeFields = (startField, endField) => (
    <div className="mr-grid mr-g2">
      {dateField(startField, startField, endField, "member_report_from")}
      {dateField(endField, startField, endField, "member_report_to")}
    </div>
  );

  const rufqaWho = (value, onChange, tone) => (
    <div>
      <div className="mr-sub-label">{t("member_report_rufqa_who")}</div>
      <Segmented
        tone={tone}
        value={value}
        onChange={onChange}
        options={[
          { value: true, label: t("member_report_attended"), icon: <FiCheck size={13} /> },
          { value: false, label: t("member_report_not_attended") },
        ]}
      />
    </div>
  );

  return (
    <Form onSubmit={handleSubmit} className="mr-panel">
      <div className="mr-panel-head">
        <span className="mr-icon-tile mr-tone-teal">
          <FiSliders size={17} />
        </span>
        <div>
          <h2>{t("member_report_filters")}</h2>
          <p>{t("member_report_filters_hint")}</p>
        </div>
        <div className="mr-push">
          <button type="button" className="mr-link" onClick={handleReset}>
            <FiRotateCcw size={13} /> {t("member_report_reset_all")}
          </button>
        </div>
      </div>

      <div className="mr-panel-body">
        <div className="mr-section">
          <SectionTitle
            icon={<FiSearch size={12} />}
            tone="teal"
            title={t("member_report_section_find")}
            hint={t("member_report_section_find_hint")}
          />
          <div className="mr-grid mr-g5">
            {textField("name", "member_report_member_name", "member_report_ph_name", <FiUser size={15} />, true)}
            {textField(
              "fatherName",
              "member_report_member_fatherName",
              "member_report_ph_father",
              <FiUser size={15} />
            )}
            {textField("regNo", "member_report_member_registrationNumber", "member_report_ph_regno", <FiHash size={15} />)}
            {textField("phoneNo", "member_report_phone_email", "member_report_ph_phone", <FiPhone size={15} />)}
            {textField("address", "member_report_address", "member_report_ph_address", <FiMapPin size={15} />)}
          </div>
        </div>

        <div className="mr-section">
          <SectionTitle
            icon={<FiLayers size={12} />}
            tone="indigo"
            title={t("member_report_section_unit")}
            hint={t("member_report_section_unit_hint")}
          />
          <div className="mr-unit-path">
            {unitList.map((record, index) => {
              const options = record.unitList.map((d) => ({ value: d.id, label: d.name }));
              const selected = record.selectedUnit
                ? { value: record.selectedUnit.id, label: record.selectedUnit.name }
                : null;
              return (
                <React.Fragment key={index}>
                  {index > 0 && (
                    <span className="mr-sep">
                      <FiChevronRight size={16} />
                    </span>
                  )}
                  <div className="mr-level">
                    <label>
                      {record.parentUnitName
                        ? t("member_select_unit_name", { value: record.parentUnitName })
                        : t("member_select_unit")}
                    </label>
                    <Select
                      options={options}
                      value={selected}
                      className="basic-single ur"
                      classNamePrefix="select"
                      styles={selectStyles()}
                      placeholder={t("member_select_unit")}
                      onChange={handleChangeUnit}
                    />
                  </div>
                </React.Fragment>
              );
            })}
          </div>
          {values.unitId !== 0 && (
            <div className="mr-toggles">
              <Toggle
                on={values.isSubUnits}
                onChange={(on) => set({ isSubUnits: on })}
                title={t("member_report_toggle_sub_units")}
                hint={t("member_report_toggle_sub_units_hint")}
              />
              <Toggle
                on={values.ui.showExUnits}
                onChange={(on) => setUi({ showExUnits: on })}
                title={t("member_report_toggle_ex_units")}
                hint={t("member_report_toggle_ex_units_hint")}
              />
              <Toggle
                on={values.includeEXMember}
                onChange={(on) => set({ includeEXMember: on })}
                title={t("member_report_toggle_ex_members")}
                hint={t("member_report_toggle_ex_members_hint")}
              />
            </div>
          )}
        </div>

        <div className="mr-section">
          <SectionTitle icon={<FiAward size={12} />} tone="violet" title={t("member_report_section_role")} />
          <div className="mr-grid mr-g2">
            {multiSelect(
              "member_report_responsibility",
              responsibilityOptions,
              values.ui.responsibilities,
              handleMultiSelect("responsibilityIds", "responsibilities"),
              "member_report_ph_responsibility"
            )}
            {multiSelect(
              "member_report_member_memberType",
              memberTypeOptions,
              values.ui.memberTypes,
              handleMultiSelect("memberTypeIds", "memberTypes"),
              "member_report_ph_member_type"
            )}
          </div>
        </div>

        <div className="mr-section">
          <SectionTitle
            icon={<FiFilter size={12} />}
            tone="amber"
            title={t("member_report_section_advanced")}
            hint={t("member_report_optional")}
          />
          <div className="mr-adv">
            {values.ui.eventsOn ? (
              <div className="mr-adv-card mr-violet">
                <button type="button" className="mr-adv-head" onClick={() => toggleEvents(false)}>
                  <span className="mr-icon-tile mr-tone-violet">
                    <FiCalendar size={16} />
                  </span>
                  <b>{t("member_report_events_title")}</b>
                  <span className="mr-sw mr-on" />
                </button>
                <div className="mr-adv-body">
                  {rufqaWho(values.includeEvent, (v) => set({ includeEvent: v, attendanceStatus: 1 }), "violet")}
                  {values.includeEvent && (
                    <div>
                      <div className="mr-sub-label">{t("events_attendance_attendance_status")}</div>
                      <div className="mr-row-inline">
                        <Segmented
                          tone="green"
                          value={values.attendanceStatus}
                          onChange={(v) => set({ attendanceStatus: v })}
                          options={[
                            { value: 1, label: t("events_participants_attendance_present") },
                            { value: 0, label: t("events_participants_attendance_absent") },
                            { value: 2, label: t("events_participants_attendance_leave") },
                          ]}
                        />
                        <Toggle
                          on={values.isOnTime}
                          disabled={values.attendanceStatus !== 1}
                          onChange={(on) => set({ isOnTime: on })}
                          title={t("member_report_on_time_only")}
                        />
                      </div>
                    </div>
                  )}
                  {multiSelect(
                    "settings_event_type",
                    eventTypeOptions,
                    values.ui.eventTypes,
                    handleMultiSelect("eventTypeIds", "eventTypes", "eventType"),
                    "member_report_ph_event_types",
                    errors.eventType
                  )}
                  {rangeFields("eventStartDate", "eventEndDate")}
                </div>
              </div>
            ) : (
              <button type="button" className="mr-adv-tile" onClick={() => toggleEvents(true)}>
                <span className="mr-icon-tile mr-tone-violet">
                  <FiCalendar size={18} />
                </span>
                <span>
                  <b>{t("member_report_events_title")}</b>
                  <small>{t("member_report_events_desc")}</small>
                </span>
                <span className="mr-sw" />
              </button>
            )}

            {values.ui.coursesOn ? (
              <div className="mr-adv-card mr-amber">
                <button type="button" className="mr-adv-head" onClick={() => toggleCourses(false)}>
                  <span className="mr-icon-tile mr-tone-amber">
                    <FiBookOpen size={16} />
                  </span>
                  <b>{t("member_report_courses_title")}</b>
                  <span className="mr-sw mr-on" />
                </button>
                <div className="mr-adv-body">
                  {rufqaWho(values.includeCourse, (v) => set({ includeCourse: v }), "amber")}
                  {values.includeCourse && (
                    <div>
                      <div className="mr-sub-label">{t("member_report_course_status")}</div>
                      <div className="mr-row-inline">
                        <label className="mr-check">
                          <input
                            type="checkbox"
                            checked={values.complete}
                            onChange={(e) => set({ complete: e.target.checked })}
                          />
                          {t("courses_status_completed")}
                        </label>
                        <label className="mr-check">
                          <input
                            type="checkbox"
                            checked={values.inComplete}
                            onChange={(e) => set({ inComplete: e.target.checked })}
                          />
                          {t("courses_status_incompleted")}
                        </label>
                      </div>
                    </div>
                  )}
                  {multiSelect(
                    "settings_course_type",
                    courseTypeOptions,
                    values.ui.courseTypes,
                    handleMultiSelect("courseTypeIds", "courseTypes", "courseType"),
                    "member_report_ph_course_types",
                    errors.courseType
                  )}
                  {rangeFields("courseStartDate", "courseEndDate")}
                </div>
              </div>
            ) : (
              <button type="button" className="mr-adv-tile" onClick={() => toggleCourses(true)}>
                <span className="mr-icon-tile mr-tone-amber">
                  <FiBookOpen size={18} />
                </span>
                <span>
                  <b>{t("member_report_courses_title")}</b>
                  <small>{t("member_report_courses_desc")}</small>
                </span>
                <span className="mr-sw" />
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="mr-actionbar">
        <div className="mr-summary">
          <span className="mr-count">{chips.length}</span>
          {t("member_report_filters_applied")}
          {chips.length > 0 && <span>· {chips.map((c) => t(c.text || c.label)).join(" · ")}</span>}
          {errorCount > 0 && (
            <span className="mr-pill mr-pill-rose">
              <FiAlertCircle size={12} /> {t("member_report_fix_fields")}
            </span>
          )}
        </div>
        <button type="button" className="mr-btn mr-btn-ghost" onClick={handleReset}>
          {t("member_report_reset")}
        </button>
        <button type="submit" className="mr-btn mr-btn-primary">
          {t("common_view_report")} <FiArrowRight size={15} className="mr-flip" />
        </button>
      </div>
    </Form>
  );
};

export default SearchReport;
